import React, { useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Palette, RotateCcw, Trash2, ShieldCheck, EyeOff, Eye } from "lucide-react";

/**
 * Admin inline actions for a report — reused by AdminDashboard rows AND
 * ReportDetail (so admins/moderators can moderate directly from the map).
 * Backend permission gates (PATCH /reports/{id} → moderator|admin,
 * DELETE /reports/{id} → admin) are the source of truth; the UI only
 * hides controls the current user cannot use.
 */
const RISK_COLOR = { green: "#10B981", yellow: "#EAB308", red: "#EF4444" };
const STATUS_OPTIONS = [
  { v: "published", labelKey: "st_published" },
  { v: "pending_moderation", labelKey: "st_pending" },
  { v: "in_progress", labelKey: "st_in_progress" },
  { v: "in_review", labelKey: "st_in_review" },
  { v: "resolved", labelKey: "st_resolved" },
  { v: "reopened", labelKey: "st_reopened" },
  { v: "archived", labelKey: "st_archived" },
  { v: "rejected", labelKey: "st_rejected" },
  { v: "hidden", labelKey: "st_hidden" },
];

export function ChangeReportColorButton({ report, onSave, t }) {
  const [open, setOpen] = useState(false);
  const [risk, setRisk] = useState(report.risk_level);
  const [reason, setReason] = useState("");
  const save = () => { onSave(risk, reason); setOpen(false); setReason(""); };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`change-color-${report.report_id}`} size="sm" variant="outline" className="rounded-full h-8 text-xs hover:bg-slate-900 hover:text-white">
          <Palette size={12} className="mr-1" /> {t("color_short")}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{t("change_color_title")}</DialogTitle></DialogHeader>
        <div className="text-xs text-slate-500">{t("current_color")} <b className="uppercase" style={{ color: RISK_COLOR[report.risk_level] }}>{report.risk_level}</b></div>
        <div className="grid grid-cols-3 gap-2 mt-2">
          {["green", "yellow", "red"].map((c) => (
            <button key={c} type="button" data-testid={`color-option-${c}-${report.report_id}`} onClick={() => setRisk(c)}
              className={`p-4 rounded-xl border-2 flex flex-col items-center gap-2 ${risk === c ? "border-slate-900 bg-slate-50" : "border-slate-200"}`}>
              <span className="w-6 h-6 rounded-full" style={{ background: RISK_COLOR[c] }} />
              <span className="text-xs font-semibold capitalize">{t(c) || c}</span>
            </button>
          ))}
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1.5">{t("reason_optional")}</div>
          <Textarea data-testid={`color-reason-${report.report_id}`} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t("color_reason_placeholder")} className="rounded-xl" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-full hover:bg-slate-100">{t("cancel")}</Button>
          <Button data-testid={`color-save-${report.report_id}`} onClick={save} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white">{t("apply")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ChangeReportStateButton({ report, onSave, t }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(report.status);
  const [reason, setReason] = useState("");
  const save = () => { onSave(status, reason); setOpen(false); setReason(""); };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`change-state-${report.report_id}`} size="sm" variant="outline" className="rounded-full h-8 text-xs hover:bg-slate-900 hover:text-white">
          {t("state_arrow")}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{t("change_state_title")}</DialogTitle></DialogHeader>
        <div className="text-xs text-slate-500">{t("current_status")} <b className="text-slate-900">{report.status}</b></div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid={`state-select-${report.report_id}`} className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
          <SelectContent>{STATUS_OPTIONS.map((s) => <SelectItem key={s.v} value={s.v}>{t(s.labelKey) || s.v}</SelectItem>)}</SelectContent>
        </Select>
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1.5">{t("reason_optional")}</div>
          <Textarea data-testid={`state-reason-${report.report_id}`} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t("state_reason_placeholder")} className="rounded-xl" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-full hover:bg-slate-100">{t("cancel")}</Button>
          <Button data-testid={`state-save-${report.report_id}`} onClick={save} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white">{t("confirm")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Bar of admin actions embedded inline on the report detail. Rendered only
 * for authenticated moderators/admins. Actions are hidden from citizens
 * regardless of the client render — the backend re-checks role on every call.
 */
export default function AdminReportActionsBar({ report, user, onDone }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  if (!user || (user.role !== "admin" && user.role !== "moderator")) return null;

  const patch = async (payload) => {
    setBusy(true);
    try {
      await api.patch(`/reports/${report.report_id}`, payload);
      toast.success(t("report_updated"));
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("generic_error"));
    } finally { setBusy(false); }
  };

  const removeReport = async () => {
    setBusy(true);
    try {
      await api.delete(`/reports/${report.report_id}`);
      toast.success(t("report_deleted"));
      onDone && onDone("deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("generic_error"));
    } finally { setBusy(false); }
  };

  const canDelete = user.role === "admin";
  const isPending = report.status === "pending_moderation";

  return (
    <div
      className="rounded-2xl border-2 border-slate-900 bg-slate-50 p-4 shadow-sm"
      data-testid="admin-actions-bar"
    >
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck size={14} className="text-slate-800" />
        <div className="text-[10px] uppercase tracking-widest text-slate-700 font-bold">
          {t("admin_actions_title")}
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {isPending && (
          <>
            <Button
              size="sm"
              data-testid="admin-map-approve"
              disabled={busy}
              onClick={() => patch({ status: "published" })}
              className="rounded-full h-8 bg-emerald-600 hover:bg-emerald-700 hover:text-white text-white text-xs"
            >
              {t("approve")}
            </Button>
            <Button
              size="sm"
              variant="outline"
              data-testid="admin-map-reject"
              disabled={busy}
              onClick={() => patch({ status: "rejected" })}
              className="rounded-full h-8 text-xs hover:bg-red-50 hover:text-red-800"
            >
              {t("reject")}
            </Button>
          </>
        )}
        <ChangeReportColorButton report={report} onSave={(risk, reason) => patch({ risk_level: risk, reason })} t={t} />
        <ChangeReportStateButton report={report} onSave={(status, reason) => patch({ status, reason })} t={t} />
        <Button
          size="sm"
          variant="outline"
          data-testid="admin-map-toggle-sensitive"
          disabled={busy}
          onClick={() => patch({ contains_sensitive: !report.contains_sensitive })}
          className="rounded-full h-8 text-xs hover:bg-orange-50 hover:text-orange-800"
        >
          {report.contains_sensitive ? <><Eye size={12} className="mr-1" /> {t("make_public")}</> : <><EyeOff size={12} className="mr-1" /> {t("mark_sensitive")}</>}
        </Button>
        {["resolved", "archived", "rejected"].includes(report.status) && (
          <Button
            size="sm"
            variant="outline"
            data-testid="admin-map-reopen"
            disabled={busy}
            onClick={() => patch({ status: "published" })}
            className="rounded-full h-8 text-xs hover:bg-emerald-50 hover:text-emerald-800"
          >
            <RotateCcw size={12} className="mr-1" /> {t("reopen")}
          </Button>
        )}
        {canDelete && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                data-testid="admin-map-delete"
                disabled={busy}
                className="rounded-full h-8 text-xs hover:bg-red-50 hover:text-red-800 text-red-700 border-red-200"
              >
                <Trash2 size={12} className="mr-1" /> {t("delete")}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="admin-map-delete-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>{t("delete_report_confirm_title")}</AlertDialogTitle>
                <AlertDialogDescription>{t("delete_report_confirm_body")}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="admin-map-delete-cancel">{t("cancel")}</AlertDialogCancel>
                <AlertDialogAction data-testid="admin-map-delete-confirm" onClick={removeReport} className="bg-red-600 hover:bg-red-700 text-white">
                  {t("delete")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    </div>
  );
}
